import { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, FormikHelpers } from 'formik';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { SignUpSchema } from 'validators/AuthenticationShema';
import {
  AppFormikTextField,
  AppFormikPasswordField,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { Toast, Utils } from 'shared/helpers';
import { AuthenticationService } from 'services';

const InvitationForm = (): ReactElement => {
  const navigate = useNavigate();
  const { invitationId } = useParams();
  const [validatingInvite, setValidatingInvite] = useState(true);
  const [inviteError, setInviteError] = useState('');
  const [signupForm, setSignupForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    invitationId,
  });

  // API Calls
  const validateInviteId = (id: string): void => {
    setValidatingInvite(true);
    setInviteError('');

    AuthenticationService.validateInvite(id)
      .then((response) => {
        const { data } = response.data;

        if (Object.keys(data).length > 0) {
          // Existing user with pending invitation
          if (data.existingUser && data.status === 'pending') {
            setValidatingInvite(false);

            AuthenticationService.acceptInvite({
              invitationId: id,
            })
              .then(() => {
                Toast.showInfoMessage(
                  'Invite accepted succesfully. Please login to use the account'
                );

                navigate('/login', {
                  replace: true,
                });
              })
              .catch((error: any) => {
                Utils.handleErrorResponse(error);
                setInviteError(error.message);
              });

            return;
          }

          // New user with pending invitation
          if (!data.existingUser && data.status === 'pending') {
            setSignupForm((prev) => ({
              ...prev,
              email: data.receiverEmail,
              invitationId: id,
            }));

            return;
          }

          // Invitation already accepted
          setInviteError('Invitation accepted already');
          return;
        }

        // No invitation data
        setInviteError('Invalid invitation');
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
        setInviteError(error.message);
      })
      .finally(() => {
        setValidatingInvite(false);
      });
  };

  // API Calls
  const signup = (formValues: any, callback: any): void => {
    AuthenticationService.acceptInvite({
      ...formValues,
      email: signupForm.email,
    })
      .then((response: any) => {
        callback(true, response);
      })
      .catch((error: any) => {
        callback(false);
        Utils.handleErrorResponse(error);
      });
  };

  // UI Actions
  const actionOnFormSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    const { name, email, password } = formValues;

    const requestObj = {
      name,
      userMail: email,
      password,
      invitationId,
    };

    signup(requestObj, (status: any) => {
      formik.setSubmitting(false);
      if (status) {
        Toast.showInfoMessage(
          'Invite accepted succesfully. Please login to use the account'
        );
        navigate('/login', {
          replace: true,
        });
      }
    });
  };

  // Hooks
  useEffect(() => {
    if (invitationId) {
      validateInviteId(`${invitationId}`);
    }
  }, [invitationId]);

  // UI Elements
  return (
    <>
      {validatingInvite && (
        <Box className="center-content flex-column">
          <CircularProgress />
          <Typography variant="h2" className="mt-10 poppins-bold">
            Validating...
          </Typography>
        </Box>
      )}
      {!validatingInvite && inviteError && (
        <Box className="center-content flex-column">
          <Typography variant="h2" className="mt-10 mb-3">
            {inviteError}
          </Typography>

          <AppPrimaryButton
            variant="contained"
            color="primary"
            title="Go to Login"
            onClick={() => {
              navigate('/login');
            }}
          />
        </Box>
      )}
      {!validatingInvite && !inviteError && (
        <Box className="signup-form-wrapper">
          <Typography className="title" variant="h2">
            Accept Invitation
          </Typography>
          <Typography className="subtitle-line-1" variant="body1">
            Complete below detail
          </Typography>

          <Box mt={7}>
            <Formik
              enableReinitialize
              initialValues={signupForm}
              validationSchema={SignUpSchema}
              validateOnMount
              onSubmit={(formValues, formik) => {
                actionOnFormSubmit(formValues, formik);
              }}
            >
              {({ handleSubmit, isSubmitting, isValid }) => (
                <form autoComplete="off">
                  <Grid container spacing={3}>
                    <Grid item sm={12} xs={12}>
                      <AppFormikTextField
                        size="small"
                        outerLabel
                        label="Name *"
                        name="name"
                      />
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <AppFormikTextField
                        size="small"
                        outerLabel
                        label="Email Address *"
                        name="email"
                        readOnly
                      />
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <AppFormikPasswordField
                        size="small"
                        type="password"
                        outerLabel
                        label="Password *"
                        name="password"
                      />
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <AppFormikPasswordField
                        size="small"
                        type="password"
                        outerLabel
                        label="Confirm Password *"
                        name="confirmPassword"
                      />
                    </Grid>
                  </Grid>

                  <Box pt={6} className="justify-center">
                    <AppPrimaryButton
                      className="btn-small"
                      title="Submit"
                      isLoading={isSubmitting}
                      loadingSize={22}
                      disabled={isSubmitting || !isValid}
                      onClick={handleSubmit}
                      fullWidth
                    />
                  </Box>
                </form>
              )}
            </Formik>
          </Box>
        </Box>
      )}
    </>
  );
};

export default InvitationForm;
